import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import API_BASE from '../config';

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

export default function SignHireDocument() {
  const { token } = useParams();
  const [state, setState]           = useState('loading'); // loading | ready | signing | signed | error | already
  const [candidate, setCandidate]   = useState(null);
  const [errorMsg, setErrorMsg]     = useState('');
  const [confirmed, setConfirmed]   = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_BASE}/recruitment/sign/${token}`);
        const data = await res.json();
        if (res.status === 410 && data.alreadySigned) { setState('already'); return; }
        if (!res.ok) { setErrorMsg(data.message || 'Invalid link'); setState('error'); return; }
        setCandidate(data.candidate);
        setState('ready');
      } catch {
        setErrorMsg('Cannot reach the server. Please try again later.');
        setState('error');
      }
    };
    load();
  }, [token]);

  const handleSign = async () => {
    if (!confirmed) return;
    setState('signing');
    try {
      const res = await fetch(`${API_BASE}/recruitment/sign/${token}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fullName: candidate?.fullName }),
      });
      const data = await res.json();
      if (!res.ok) { setErrorMsg(data.message || 'Failed to sign'); setState('error'); return; }
      setState('signed');
    } catch {
      setErrorMsg('Server error. Please try again.');
      setState('error');
    }
  };

  const wrap = (children) => (
    <div style={{ minHeight: '100vh', background: '#f0f4ff', display: 'flex', alignItems: 'flex-start', justifyContent: 'center', padding: '40px 16px', fontFamily: 'Arial, sans-serif' }}>
      <div style={{ background: '#fff', borderRadius: 12, padding: '36px 40px', maxWidth: 600, width: '100%', boxShadow: '0 4px 32px rgba(0,0,0,0.10)' }}>
        {children}
      </div>
    </div>
  );

  if (state === 'loading') return wrap(
    <div style={{ textAlign: 'center', padding: 40, color: '#555' }}>⏳ Loading your document…</div>
  );

  if (state === 'error') return wrap(
    <>
      <div style={{ textAlign: 'center', marginBottom: 20 }}>
        <span style={{ fontSize: 48 }}>⚠️</span>
      </div>
      <h2 style={{ textAlign: 'center', color: '#dc2626', marginTop: 0 }}>Invalid or Expired Link</h2>
      <p style={{ textAlign: 'center', color: '#555' }}>{errorMsg}</p>
      <p style={{ textAlign: 'center', fontSize: 13, color: '#888' }}>
        If you believe this is an error, please contact your HR representative.
      </p>
    </>
  );

  if (state === 'already') return wrap(
    <>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 52 }}>✅</span>
      </div>
      <h2 style={{ textAlign: 'center', color: '#166534', marginTop: 0 }}>Already Signed</h2>
      <p style={{ textAlign: 'center', color: '#555' }}>
        Your employment documents have already been signed. No further action is required.
      </p>
      <p style={{ textAlign: 'center', fontSize: 13, color: '#888' }}>
        If you have questions, please contact your HR representative.
      </p>
    </>
  );

  if (state === 'signed') return wrap(
    <>
      <div style={{ textAlign: 'center', marginBottom: 16 }}>
        <span style={{ fontSize: 52 }}>🎉</span>
      </div>
      <h2 style={{ textAlign: 'center', color: '#166534', marginTop: 0 }}>Documents Signed Successfully!</h2>
      <p style={{ textAlign: 'center', color: '#555', fontSize: 15 }}>
        Thank you, <strong>{candidate?.fullName}</strong>. Your acceptance has been recorded.
      </p>
      <p style={{ textAlign: 'center', fontSize: 13, color: '#888' }}>
        Our HR team has been notified. You will hear from us soon regarding your start date and equipment.
      </p>
      <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '14px 18px', marginTop: 20, fontSize: 13, color: '#166534' }}>
        <strong>What happens next?</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, lineHeight: 1.9 }}>
          <li>HR will prepare your onboarding schedule</li>
          <li>IT will arrange equipment assignment</li>
          <li>You'll receive further details by email</li>
        </ul>
      </div>
    </>
  );

  // state === 'ready' or 'signing'
  const c = candidate;
  return wrap(
    <>
      {/* Company header */}
      <div style={{ background: 'linear-gradient(135deg,#1a237e,#283593)', borderRadius: 8, padding: '20px 24px', marginBottom: 24, color: '#fff' }}>
        <h2 style={{ margin: 0, fontSize: 20 }}>Employment Welcome Document</h2>
        <p style={{ margin: '6px 0 0 0', fontSize: 13, opacity: 0.85 }}>Please review your details and sign below to confirm acceptance</p>
      </div>

      {/* Candidate details */}
      <div style={{ background: '#f8faff', border: '1px solid #c5cae9', borderRadius: 8, padding: '18px 22px', marginBottom: 22 }}>
        <h3 style={{ margin: '0 0 14px 0', color: '#1a237e', fontSize: 15 }}>Your Details</h3>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <tbody>
            {[
              ['Full Name',         c?.fullName],
              ['Email',             c?.email],
              ['Telephone',         c?.telephone],
              ['Address',           c?.address],
              ['Campaign / Role',   c?.campaign],
              ['Start Date',        fmtDate(c?.startDate)],
              ['Salary (Gross/Mo)', c?.salaryExpectation],
              ['Country',           c?.country?.name],
            ].filter(([, v]) => v && v !== '—').map(([label, value]) => (
              <tr key={label} style={{ borderBottom: '1px solid #e8eaf6' }}>
                <td style={{ padding: '7px 0', color: '#666', fontWeight: 600, width: '40%' }}>{label}</td>
                <td style={{ padding: '7px 0', color: '#222' }}>{value}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Terms summary */}
      <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, padding: '14px 18px', marginBottom: 22, fontSize: 13, color: '#78350f' }}>
        <strong>By signing, you confirm:</strong>
        <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, lineHeight: 2 }}>
          <li>All your personal details shown above are correct</li>
          <li>You accept the terms outlined in the attached employment document</li>
          <li>You agree to commence employment on the agreed start date</li>
        </ul>
      </div>

      {/* Checkbox */}
      <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 22, cursor: 'pointer', fontSize: 14, color: '#333' }}>
        <input
          type="checkbox"
          checked={confirmed}
          onChange={e => setConfirmed(e.target.checked)}
          style={{ marginTop: 3, width: 16, height: 16, flexShrink: 0, cursor: 'pointer' }}
        />
        I, <strong style={{ marginLeft: 4 }}>{c?.fullName}</strong>, confirm that I have read and understood this document and I accept the offer of employment.
      </label>

      {/* Sign button */}
      <button
        onClick={handleSign}
        disabled={!confirmed || state === 'signing'}
        style={{
          width: '100%', padding: '14px', background: confirmed ? '#1a237e' : '#9ca3af',
          color: '#fff', border: 'none', borderRadius: 8, fontSize: 16, fontWeight: 700,
          cursor: confirmed ? 'pointer' : 'not-allowed', transition: 'background 0.2s',
        }}
      >
        {state === 'signing' ? '⏳ Processing signature…' : '✍️ Sign & Accept Document'}
      </button>

      <p style={{ textAlign: 'center', fontSize: 11, color: '#aaa', marginTop: 16 }}>
        This electronic signature is legally binding. Date: {new Date().toLocaleDateString('en-GB')}.
      </p>
    </>
  );
}
